import { useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { 
  Shield, ShieldCheck, AlertTriangle, CheckCircle2, 
  Clock, Loader2, ExternalLink, User, FileCheck
} from 'lucide-react';
import api from '@/utils/api';

// Load Stripe with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_emergent');

/**
 * Trust Badge Component - Shows verification status
 */
export const TrustBadge = ({ user, size = 'sm', showLabel = true }) => {
  const isVerified = user?.kyc_status === 'verified';
  const isEmailVerified = user?.email_verified;
  
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };
  
  if (isVerified) {
    return (
      <div className="flex items-center gap-1" title="Identity Verified">
        <ShieldCheck className={`${sizeClasses[size]} text-emerald-500`} />
        {showLabel && <span className="text-xs font-medium text-emerald-600">Verified</span>}
      </div>
    );
  }
  
  if (isEmailVerified) {
    return (
      <div className="flex items-center gap-1" title="Email Verified">
        <Shield className={`${sizeClasses[size]} text-blue-500`} />
        {showLabel && <span className="text-xs font-medium text-blue-600">Email Verified</span>}
      </div>
    );
  }
  
  return null;
};

/**
 * Seller Trust Card - Shows detailed trust info on item/profile pages
 */
export const SellerTrustCard = ({ sellerId, className = '' }) => {
  const [trustInfo, setTrustInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchTrustInfo = async () => {
      try {
        const res = await api.get(`/users/${sellerId}/trust-info`);
        setTrustInfo(res.data);
      } catch (error) {
        console.error('Failed to fetch trust info:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (sellerId) {
      fetchTrustInfo();
    }
  }, [sellerId]);
  
  if (loading) {
    return (
      <Card className={`p-4 ${className}`}>
        <div className="animate-pulse flex items-center gap-3">
          <div className="h-10 w-10 bg-slate-200 rounded-full"></div>
          <div className="space-y-2 flex-1">
            <div className="h-4 bg-slate-200 rounded w-1/2"></div>
            <div className="h-3 bg-slate-200 rounded w-1/3"></div>
          </div>
        </div>
      </Card>
    );
  }
  
  if (!trustInfo) return null;
  
  const trustLevelColors = {
    excellent: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    good: 'bg-blue-100 text-blue-700 border-blue-200',
    fair: 'bg-amber-100 text-amber-700 border-amber-200',
    new: 'bg-slate-100 text-slate-700 border-slate-200'
  };
  
  return (
    <Card className={`p-4 ${className}`}>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm">Seller Trust</h4>
          <Badge className={trustLevelColors[trustInfo.trust_level]}>
            {trustInfo.trust_level.charAt(0).toUpperCase() + trustInfo.trust_level.slice(1)}
          </Badge>
        </div>
        
        {/* Trust Score Bar */}
        <div>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-slate-500">Trust Score</span>
            <span className="font-medium">{trustInfo.trust_score}/100</span>
          </div>
          <Progress value={trustInfo.trust_score} className="h-2" />
        </div>
        
        {/* Badges */}
        <div className="flex flex-wrap gap-2">
          {trustInfo.badges.identity_verified && (
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
              <ShieldCheck className="h-3 w-3 mr-1" />
              ID Verified
            </Badge>
          )}
          {trustInfo.badges.email_verified && (
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Email Verified
            </Badge>
          )}
          {trustInfo.badges.trusted_seller && (
            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
              <Shield className="h-3 w-3 mr-1" />
              Trusted Seller
            </Badge>
          )}
          {trustInfo.badges.top_rated && (
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
              ⭐ Top Rated
            </Badge>
          )}
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t">
          <div>
            <p className="text-lg font-semibold">{trustInfo.stats.sales_count}</p>
            <p className="text-xs text-slate-500">Sales</p>
          </div>
          <div>
            <p className="text-lg font-semibold">{trustInfo.stats.avg_rating || '-'}</p>
            <p className="text-xs text-slate-500">Rating</p>
          </div>
          <div>
            <p className="text-lg font-semibold">{trustInfo.stats.review_count}</p>
            <p className="text-xs text-slate-500">Reviews</p>
          </div>
        </div>
      </div>
    </Card>
  );
};

/**
 * KYC Status Card - Shows current verification status in account page
 */
export const KYCStatusCard = ({ user, onStartVerification }) => {
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await api.get('/kyc/status');
        setKycStatus(res.data);
      } catch (error) {
        console.error('Failed to fetch KYC status:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (user) {
      fetchStatus();
    }
  }, [user]);
  
  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-6 bg-slate-200 rounded w-1/3"></div>
          <div className="h-4 bg-slate-200 rounded w-2/3"></div>
        </div>
      </Card>
    );
  }
  
  const status = kycStatus?.kyc_status || 'none';
  
  const statusConfig = {
    none: {
      icon: Shield,
      color: 'text-slate-400',
      bgColor: 'bg-slate-50',
      borderColor: 'border-slate-200',
      title: 'Not Verified',
      description: 'Verify your identity to unlock all features and build trust with buyers.'
    },
    pending: {
      icon: Clock,
      color: 'text-amber-500',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      title: 'Verification Pending',
      description: 'Your verification is being processed. This usually takes a few minutes.'
    },
    processing: {
      icon: Loader2,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      title: 'Processing',
      description: 'We are reviewing your documents. Please wait.'
    },
    verified: {
      icon: ShieldCheck,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      title: 'Identity Verified',
      description: 'Your identity has been verified. You have full access to all features.'
    },
    failed: {
      icon: AlertTriangle,
      color: 'text-red-500',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      title: 'Verification Failed',
      description: 'We could not verify your identity. Please try again with valid documents.'
    }
  };
  
  const config = statusConfig[status] || statusConfig.none;
  const StatusIcon = config.icon;
  
  return (
    <Card className={`p-6 ${config.bgColor} border ${config.borderColor}`}>
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-full ${config.bgColor}`}>
          <StatusIcon className={`h-8 w-8 ${config.color} ${status === 'processing' ? 'animate-spin' : ''}`} />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg">{config.title}</h3>
          <p className="text-sm text-slate-600 mt-1">{config.description}</p>
          
          {/* Trust Score */}
          {kycStatus?.trust_score !== undefined && (
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">Trust Score</span>
                <span className="font-medium">{kycStatus.trust_score}/100</span>
              </div>
              <Progress value={kycStatus.trust_score} className="h-2" />
            </div>
          )}
          
          {/* Trust Indicators */}
          {kycStatus?.trust_indicators && (
            <div className="flex flex-wrap gap-2 mt-4">
              {kycStatus.trust_indicators.email_verified && (
                <Badge variant="outline" className="bg-white">
                  <CheckCircle2 className="h-3 w-3 mr-1 text-emerald-500" />
                  Email Verified
                </Badge>
              )}
              {kycStatus.trust_indicators.kyc_verified && (
                <Badge variant="outline" className="bg-white">
                  <ShieldCheck className="h-3 w-3 mr-1 text-emerald-500" />
                  ID Verified
                </Badge>
              )}
              {kycStatus.trust_indicators.has_sales_history && (
                <Badge variant="outline" className="bg-white">
                  Sales History
                </Badge>
              )}
            </div>
          )}
          
          {/* Action Button */}
          {(status === 'none' || status === 'failed') && (
            <Button 
              onClick={onStartVerification}
              className="mt-4 bg-emerald-500 hover:bg-emerald-600"
            >
              <Shield className="h-4 w-4 mr-2" />
              Start Verification
            </Button>
          )}
          
          {status === 'pending' && (
            <Button 
              variant="outline"
              onClick={onStartVerification}
              className="mt-4"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Continue Verification
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

/**
 * KYC Verification Modal - Handles the verification flow
 */
export const KYCVerificationModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState('intro'); // intro, verifying, success, error
  const [verificationId, setVerificationId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const startVerification = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Create verification session
      const res = await api.post('/kyc/start-verification');
      
      if (res.data.status === 'verified') {
        setStep('success');
        onSuccess?.();
        return;
      }
      
      setVerificationId(res.data.verification_id);
      
      // Load Stripe and open verification modal
      const stripe = await stripePromise;
      
      if (!stripe) {
        throw new Error('Failed to load Stripe');
      }
      
      setStep('verifying');
      
      // Use Stripe's verifyIdentity method
      const result = await stripe.verifyIdentity(res.data.client_secret);
      
      if (result.error) {
        setError(result.error.message);
        setStep('error');
      } else {
        // Poll for verification status
        setStep('processing');
        pollVerificationStatus(res.data.verification_id);
      }
      
    } catch (err) {
      console.error('KYC error:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to start verification');
      setStep('error');
    } finally {
      setLoading(false);
    }
  }, [onSuccess]);
  
  const pollVerificationStatus = async (verId) => {
    let attempts = 0;
    const maxAttempts = 30; // 5 minutes with 10-second intervals
    
    const poll = async () => {
      try {
        const res = await api.get(`/kyc/check/${verId}`);
        
        if (res.data.status === 'verified') {
          setStep('success');
          toast.success('Identity verified successfully!');
          onSuccess?.();
          return;
        } else if (res.data.status === 'failed' || res.data.status === 'cancelled') {
          setError('Verification was not successful. Please try again.');
          setStep('error');
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000); // Poll every 10 seconds
        } else {
          setStep('success');
          toast.info('Verification submitted. Status will update shortly.');
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    };
    
    poll();
  };
  
  const renderContent = () => {
    switch (step) {
      case 'intro':
        return (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-6 w-6 text-emerald-500" />
                Verify Your Identity
              </DialogTitle>
              <DialogDescription>
                Verify your identity to build trust with buyers and unlock all features.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-6 space-y-4">
              <div className="bg-emerald-50 rounded-lg p-4">
                <h4 className="font-semibold text-emerald-800 mb-2">Benefits of Verification</h4>
                <ul className="text-sm text-emerald-700 space-y-1">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Get a verified badge on your profile
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Sell high-value items (over $500)
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Increase buyer trust and conversion
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Priority listing in search results
                  </li>
                </ul>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold mb-2">What You'll Need</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className="flex items-center gap-2">
                    <FileCheck className="h-4 w-4 text-slate-400" />
                    Government-issued ID (passport, driver's license, or ID card)
                  </li>
                  <li className="flex items-center gap-2">
                    <User className="h-4 w-4 text-slate-400" />
                    A selfie for identity matching
                  </li>
                </ul>
              </div>
              
              <p className="text-xs text-slate-500">
                Your information is securely processed by Stripe Identity. We never store your ID documents.
              </p>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button 
                onClick={startVerification} 
                disabled={loading}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Shield className="h-4 w-4 mr-2" />
                    Start Verification
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        );
        
      case 'verifying':
      case 'processing':
        return (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
                Verifying Your Identity
              </DialogTitle>
            </DialogHeader>
            <div className="py-8 text-center">
              <p className="text-slate-600">
                {step === 'verifying' 
                  ? 'Please complete the verification in the popup window...'
                  : 'Processing your verification. This may take a moment...'
                }
              </p>
            </div>
          </>
        );
        
      case 'success':
        return (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="h-6 w-6 text-emerald-500" />
                Verification Complete!
              </DialogTitle>
            </DialogHeader>
            <div className="py-8 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="h-8 w-8 text-emerald-500" />
              </div>
              <p className="text-slate-600 mb-4">
                Your identity has been verified successfully. You now have full access to all features.
              </p>
              <Button onClick={onClose} className="bg-emerald-500 hover:bg-emerald-600">
                Done
              </Button>
            </div>
          </>
        );
        
      case 'error':
        return (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-6 w-6 text-red-500" />
                Verification Failed
              </DialogTitle>
            </DialogHeader>
            <div className="py-8 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="h-8 w-8 text-red-500" />
              </div>
              <p className="text-slate-600 mb-4">
                {error || 'We could not complete your verification. Please try again.'}
              </p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button onClick={() => setStep('intro')} className="bg-emerald-500 hover:bg-emerald-600">
                  Try Again
                </Button>
              </div>
            </div>
          </>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default KYCVerificationModal;
